import {
  OnlyIElementsStore,
  selectIElementsSlice,
  selectVideoModeCopyTag,
} from "@/store/ielements-slice-selector";
import {
  selectLightweightElements,
  selectLightweightElementsSlice,
} from "@/store/lightweight-elements-selectors";
import {
  LightweightElementsRecord,
  LightweightElementsState,
} from "@/store/lightweight-elements-slice";
import {
  selectProjectFilteringSlice,
  selectSearchTags,
  selectSearchTextMatchingTagIds,
} from "@/store/project-filtering/project-filtering-selectors";
import { ProjectFilteringSlice } from "@/store/project-filtering/project-filtering-slice";
import { curryAppSelector } from "@/store/reselect";
import { selectSelectionsSlice } from "@/store/selections-selectors";
import { HiddenScans, SelectionState } from "@/store/selections-slice";
import { selectTagsSlice } from "@/store/tags/tags-selectors";
import { TagsState } from "@/store/tags/tags-slice";
import { IElementPhotogrammetryScan } from "@/types/ielement-extra-types";
import { TreeData } from "@/utils/tree-generation/tree-data";
import {
  filterElement,
  FilterValues,
} from "@/utils/tree-generation/tree-filters-utils";
import { assert, GUID } from "@faro-lotv/foundation";
import {
  IElementGenericOdometryPath,
  IElementImg2d,
  IElementImg360,
  IElementRoomSection,
  isIElementSection,
  isIElementSectionDataSession,
  isIElementTimeSeriesRoom,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  isSingleScanSection,
  selectAncestor,
  selectIElement,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { uniqBy } from "es-toolkit";
import { CurrentAreaData } from "./select-current-area-data";

type FilteredSceneState = {
  iElements: IElementsState;
  selections: SelectionState;
  projectFiltering: ProjectFilteringSlice;
  lightweightElements: LightweightElementsState;
  tags: TagsState;
};

/** All data that should be rendered in the current scene, after filtering by tags and visibility */
type CurrentSceneFiltered = {
  /** Pano images visible in the current scene */
  filteredPanos: IElementImg360[];
  /** 2D photos visible in the current scene */
  filteredPhotos: IElementImg2d[];
  /** Orthomosaics visible in the current scene */
  filteredOrthomosaics: IElementPhotogrammetryScan[];
  /** Location markers visible in the current scene */
  filteredLocationMarkers: IElementRoomSection[];
  /** Paths visible in the current scene */
  filteredPaths: IElementGenericOdometryPath[];
};

/**
 * This selector takes as input data from the current static scene, and filters them according to
 * the current tags and visibility selections.
 *
 * @param state the app store state
 * @param areaData the current area data
 * @param treeData the current representation of the project capture tree
 * @param staticPanos the list of panos visible in the static scene
 * @param paths the list of paths visible in the static scene
 * @param photos the list of photos visible in the static scene
 * @param orthomosaics the list of orthomosaics visible in the static scene
 * @returns the memoized selector
 */
export const selectCurrentSceneFiltered = createSelector(
  [
    selectIElementsSlice,
    (state: FilteredSceneState) => selectSelectionsSlice(state).hiddenScans,
    selectProjectFilteringSlice,
    selectLightweightElementsSlice,
    selectTagsSlice,
    (state: FilteredSceneState, areaData: CurrentAreaData) => areaData,
    (
      state: FilteredSceneState,
      areaData: CurrentAreaData,
      treeData: TreeData[] | undefined,
    ) => treeData,
    (
      state: FilteredSceneState,
      areaData: CurrentAreaData,
      treeData: TreeData[] | undefined,
      staticPanos: IElementImg360[],
    ) => staticPanos,
    (
      state: FilteredSceneState,
      areaData: CurrentAreaData,
      treeData: TreeData[] | undefined,
      staticPanos: IElementImg360[],
      paths: IElementGenericOdometryPath[],
    ) => paths,
    (
      state: FilteredSceneState,
      areaData: CurrentAreaData,
      treeData: TreeData[] | undefined,
      staticPanos: IElementImg360[],
      paths: IElementGenericOdometryPath[],
      photos: IElementImg2d[],
    ) => photos,
    (
      state: FilteredSceneState,
      areaData: CurrentAreaData,
      treeData: TreeData[] | undefined,
      staticPanos: IElementImg360[],
      paths: IElementGenericOdometryPath[],
      photos: IElementImg2d[],
      orthomosaics: IElementPhotogrammetryScan[],
    ) => orthomosaics,
  ],
  computeCurrentSceneFiltered,
);

function computeCurrentSceneFiltered(
  iElements: IElementsState,
  hiddenScans: HiddenScans,
  projectFiltering: ProjectFilteringSlice,
  lightweightElements: LightweightElementsState,
  tags: TagsState,
  areaData: CurrentAreaData,
  treeData: TreeData[] | undefined,
  staticPanos: IElementImg360[],
  paths: IElementGenericOdometryPath[],
  photos: IElementImg2d[],
  orthomosaics: IElementPhotogrammetryScan[],
): CurrentSceneFiltered {
  const videoModeCopyTag = selectVideoModeCopyTag({ iElements });

  const filteredOutIDs = selectFilteredOutIDs(
    areaData.area?.id,
    treeData ?? [],
    videoModeCopyTag,
  )({ lightweightElements, projectFiltering, tags });

  // The list of panos not hidden by the user
  let filteredPanos = staticPanos.filter(
    (pano) => !pano.parentId || !hiddenScans[pano.parentId],
  );

  if (filteredOutIDs.size > 0) {
    filteredPanos = filteredPanos.filter(
      (pano) =>
        !filteredOutIDs.has(selectFilterablePlaceholderId(pano)({ iElements })),
    );
  }

  let filteredPhotos = photos;

  if (filteredOutIDs.size > 0) {
    filteredPhotos = filteredPhotos.filter(
      (photo) =>
        !filteredOutIDs.has(
          selectFilterablePlaceholderId(photo)({ iElements }),
        ),
    );
  }

  let filteredLocationMarkers = areaData.locationMarkers;

  if (filteredOutIDs.size > 0) {
    filteredLocationMarkers = filteredLocationMarkers.filter(
      (marker) => !filteredOutIDs.has(marker.id),
    );
  }

  // It can happen that the 'paths' array contains duplicates, let's remove them
  const uniquePaths = uniqBy(paths, (el) => el.id);

  // Remove all paths manually hidden by the user
  const filteredPaths = uniquePaths.filter((t) => {
    if (filteredOutIDs.size > 0) {
      const dataSession = selectAncestor(
        t,
        isIElementSectionDataSession,
      )({ iElements });
      if (dataSession && filteredOutIDs.has(dataSession.id)) {
        return false;
      }
    }
    const scan = selectAncestor(t, isSingleScanSection)({ iElements });
    return !scan || (!hiddenScans[scan.id] && !filteredOutIDs.has(scan.id));
  });

  let filteredOrthomosaics = orthomosaics.filter(
    (orthomosaic) => !hiddenScans[orthomosaic.id],
  );

  if (filteredOutIDs.size > 0) {
    filteredOrthomosaics = filteredOrthomosaics.filter(
      (orthomosaic) => !filteredOutIDs.has(orthomosaic.id),
    );
  }

  return {
    filteredPanos,
    filteredPhotos,
    filteredOrthomosaics,
    filteredLocationMarkers,
    filteredPaths,
  };
}

type FilteredOutIDsState = {
  lightweightElements: LightweightElementsState;
  projectFiltering: ProjectFilteringSlice;
  tags: TagsState;
};

/**
 * Returns the set of IDs of the waypoints that are filtered out, and
 * that are children of the current area at any level. It uses the same code
 * used to filter the capture tree, to consolidate functionality and prevent bugs
 * inconsistencies.
 *
 * @param state The application root state
 * @param activeAreaId The id of the current active area
 * @param tree The tree data used to build the capture tree view
 * @param videoModeCopyTagId The id of the tag identifying rooms copied for video mode datasets
 * @returns The set of IDs of the waypoints that are filtered out
 */
export const selectFilteredOutIDs = curryAppSelector(
  createSelector(
    [
      selectLightweightElements,
      selectProjectFilteringSlice,
      selectSearchTags,
      selectSearchTextMatchingTagIds,
      (state: FilteredOutIDsState, activeAreaId: GUID | undefined) =>
        activeAreaId,
      (
        state: FilteredOutIDsState,
        activeAreaId: GUID | undefined,
        tree: TreeData[],
      ) => tree,
      (
        state: FilteredOutIDsState,
        activeAreaId: GUID | undefined,
        tree: TreeData[],
        videoModeCopyTagId: GUID | undefined,
      ) => videoModeCopyTagId,
    ],
    computeFilteredOutIDs,
  ),
);

function computeFilteredOutIDs(
  lightweightElements: LightweightElementsRecord,
  currentFilters: ProjectFilteringSlice,
  menuTags: GUID[] | undefined,
  textTagIds: GUID[],
  activeAreaId: GUID | undefined,
  tree: TreeData[],
  videoModeCopyTagId: GUID | undefined,
): Set<GUID> {
  const result = new Set<GUID>();
  // If there's no active area, or there are no active filters, return empty set
  if (
    !activeAreaId ||
    (!currentFilters.text &&
      (!menuTags || menuTags.length === 0) &&
      currentFilters.dateRange.start === undefined &&
      currentFilters.dateRange.end === undefined)
  ) {
    return result;
  }

  // Find the tree element corresponding to the active area
  // It wil be the root of the subtree that will be searched for
  // filtered out elements
  const areaElement = findTreeElement(tree, activeAreaId);
  if (!areaElement) return result;

  return filteredOutIDsInSubtree(
    areaElement,
    lightweightElements,
    currentFilters,
    menuTags,
    textTagIds,
    videoModeCopyTagId,
    result,
  );
}

function findTreeElement(
  tree: TreeData[],
  elementId: GUID,
): TreeData | undefined {
  const stack = new Array<TreeData>();
  stack.push(...tree);

  while (stack.length > 0) {
    const node = stack.pop();
    assert(node, "Node should be defined here");
    if (node.id === elementId) {
      return node;
    }
    if (node.children) {
      stack.push(...node.children);
    }
  }
}

type StackElement = Pick<TreeData, "id" | "element" | "children"> & {
  // The nearest tree-view-leaf ancestor, used to walk up and
  // unblock ancestors from `result` when a descendant passes the filter.
  // `null` for non-leaf tree-view nodes.
  parent: StackElement | null;
};

function filteredOutIDsInSubtree(
  areaElement: TreeData,
  lightweightElements: LightweightElementsRecord,
  currentFilters: ProjectFilteringSlice,
  menuTags: GUID[] | undefined,
  textTagIds: GUID[],
  videoModeCopyTagId: GUID | undefined,
  result: Set<GUID>,
): Set<GUID> {
  const stack = new Array<StackElement>();
  stack.push({ ...areaElement, parent: null });

  const filterValues: FilterValues = {
    text: currentFilters.text,
    dateRange: currentFilters.dateRange,
    tags: menuTags,
    textTagIds,
    videoModeCopyTagId,
  };

  const elementFilterSet = new Set<GUID>();

  while (stack.length > 0) {
    const node = stack.pop();
    assert(node, "Node should be defined here");

    if (node.children) {
      // Non-leaf tree-view nodes push their children with parent: null.
      // Only project-tree descendants of tree-view leaves (handled below)
      // need ancestor tracking to drive `result` removals.
      stack.push(...node.children.map((c) => ({ ...c, parent: null })));
    }

    const parentId = lightweightElements[node.id]?.parentId;
    const isParentShown = !!parentId && elementFilterSet.has(parentId);
    if (isParentShown) {
      elementFilterSet.add(node.id);
      continue;
    }

    const { element } = node;
    if (!element) continue;

    if (filterElement(element, lightweightElements, filterValues)) {
      elementFilterSet.add(element.id);

      // This element passes the filter — walk up the parent chain and remove
      // each ancestor from `result`. Without this, a passing time point would
      // remain hidden because its ancestor `Room-Section` was already added to
      // `result`, keeping it hidden in the scene despite being shown in the tree.
      let ancestor = node.parent;
      while (ancestor) {
        result.delete(ancestor.id);
        ancestor = ancestor.parent;
      }

      continue;
    }

    // While the TreeData doesn't provide further children to render, the corresponding project subtree should
    // still be considered for filtering.
    if (!node.children?.length) {
      // Perform fine-grained per-child filtering instead of all-or-nothing:
      // only descendants that actually fail the filter are added to result.
      // This allows to filter single waypoint from Structured E57 or Webshare datasets.
      const childrenIds = lightweightElements[node.id]?.childrenIds;
      const children = (childrenIds ?? [])
        // Video mode copies are excluded when the tree is generated, so they must not
        // make an ancestor pass the filters either
        .filter(
          (id) =>
            !videoModeCopyTagId ||
            !lightweightElements[id]?.labelIds?.includes(videoModeCopyTagId),
        )
        .map((id) => ({
          id,
          element: lightweightElements[id],
          children: null,
        }));
      stack.push(...children.map((c) => ({ ...c, parent: node })));
    }

    // This element is filtered out
    result.add(node.id);
  }
  return result;
}

const selectFilterablePlaceholderId = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, element: IElementImg360 | IElementImg2d) =>
        element,
    ],
    computeFilterablePlaceholderId,
  ),
);

function computeFilterablePlaceholderId(
  iElements: IElementsState,
  element: IElementImg360 | IElementImg2d,
): GUID {
  const iElementsState = { iElements };
  assert(element.parentId, "A pano placeholder should always have a parent");
  let parentSection = selectAncestor(
    element,
    isIElementSection,
  )(iElementsState);
  assert(
    parentSection,
    "A pano placeholder should always have a section as parent",
  );
  // if 'parentSection' is a child of a timeseries room, we select the parent of the timeseries room
  // which is the section targeted by project search&filtering
  const grandParent = selectIElement(parentSection.parentId)(iElementsState);
  assert(grandParent, "Parents of sections should always exist");
  if (isIElementTimeSeriesRoom(grandParent)) {
    const grandGrandParent = selectIElement(grandParent.parentId)(
      iElementsState,
    );
    assert(
      grandGrandParent && isIElementSection(grandGrandParent),
      "A time travel room should always have a section as parent",
    );
    parentSection = grandGrandParent;
  }
  return parentSection.id;
}
